import { create } from "zustand";
import type {
  AgentStatus,
  GitCommit,
  SessionMeta,
  TeamMember,
  WorkspaceInfo,
} from "@shared/protocol";

export type Layout = "2x2" | "1x2" | "1x3" | "full";
export type Density = "luftig" | "kompakt";
export type SidebarVariant = "workspaces" | "team";
export type TerminalStyle = "classic" | "warp";

export interface PanelState {
  agentId: string;
  task: string;
  workspacePath: string;
  workspaceName: string;
  status: AgentStatus;
  recap?: string;
  startedAt: number;
  active: boolean;
  readonly?: boolean;
  ownerSupabaseId?: string;
}

interface AppState {
  // auth
  me: TeamMember | null;
  team: TeamMember[];
  // workspaces
  workspaces: WorkspaceInfo[];
  activeWorkspace: string | null;
  // panels
  panels: PanelState[];
  // git
  commits: GitCommit[];
  // ui
  layout: Layout;
  density: Density;
  sidebarVariant: SidebarVariant;
  terminalStyle: TerminalStyle;
  wsModalOpen: boolean;
  teammateDrawer: string | null;

  setMe: (m: TeamMember | null) => void;
  setTeam: (t: TeamMember[]) => void;
  setWorkspaces: (w: WorkspaceInfo[]) => void;
  setActiveWorkspace: (id: string | null) => void;
  upsertPanel: (p: PanelState) => void;
  removePanel: (agentId: string) => void;
  patchPanel: (agentId: string, patch: Partial<PanelState>) => void;
  setActivePanel: (agentId: string) => void;
  hydrateSessions: (sessions: SessionMeta[]) => void;
  pushCommit: (c: GitCommit) => void;
  setLayout: (l: Layout) => void;
  setDensity: (d: Density) => void;
  setSidebarVariant: (v: SidebarVariant) => void;
  setTerminalStyle: (s: TerminalStyle) => void;
  openWorkspaceModal: () => void;
  closeWorkspaceModal: () => void;
  openTeammate: (supabaseId: string) => void;
  closeTeammate: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  me: null,
  team: [],
  workspaces: [],
  activeWorkspace: null,
  panels: [],
  commits: [],
  layout: "2x2",
  density: "luftig",
  sidebarVariant: "workspaces",
  terminalStyle: "classic",
  wsModalOpen: false,
  teammateDrawer: null,

  setMe: (m) => set({ me: m }),
  setTeam: (t) => set({ team: t }),
  setWorkspaces: (w) =>
    set((s) => ({ workspaces: w, activeWorkspace: s.activeWorkspace ?? w[0]?.id ?? null })),
  setActiveWorkspace: (id) => set({ activeWorkspace: id }),
  upsertPanel: (p) =>
    set((s) => {
      const idx = s.panels.findIndex((x) => x.agentId === p.agentId);
      const panels = idx >= 0 ? s.panels.map((x, i) => (i === idx ? { ...x, ...p } : x)) : [...s.panels, p];
      return { panels };
    }),
  removePanel: (agentId) => set((s) => ({ panels: s.panels.filter((p) => p.agentId !== agentId) })),
  patchPanel: (agentId, patch) =>
    set((s) => ({ panels: s.panels.map((p) => (p.agentId === agentId ? { ...p, ...patch } : p)) })),
  setActivePanel: (agentId) =>
    set((s) => ({ panels: s.panels.map((p) => ({ ...p, active: p.agentId === agentId })) })),
  hydrateSessions: (sessions) =>
    set((s) => {
      const known = new Map(s.panels.map((p) => [p.agentId, p]));
      for (const sess of sessions) {
        const existing = known.get(sess.agentId);
        known.set(sess.agentId, {
          agentId: sess.agentId,
          task: sess.task,
          workspacePath: sess.workspacePath,
          workspaceName: sess.workspaceName,
          status: sess.status,
          startedAt: sess.startedAt,
          active: existing?.active ?? false,
          ownerSupabaseId: sess.ownerSupabaseId,
        });
      }
      return { panels: Array.from(known.values()) };
    }),
  pushCommit: (c) => set((s) => ({ commits: [c, ...s.commits].slice(0, 50) })),
  setLayout: (l) => set({ layout: l }),
  setDensity: (d) => set({ density: d }),
  setSidebarVariant: (v) => set({ sidebarVariant: v }),
  setTerminalStyle: (s) => set({ terminalStyle: s }),
  openWorkspaceModal: () => set({ wsModalOpen: true }),
  closeWorkspaceModal: () => set({ wsModalOpen: false }),
  openTeammate: (id) => set({ teammateDrawer: id }),
  closeTeammate: () => set({ teammateDrawer: null }),
}));
